import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /**
   * Keep quick add compact without changing the merchant's product-page picker.
   * Run on detached markup, including every variant response, before morphing.
   * @param {Element | null} picker
   */
  static prepareQuickAdd(picker, { sizeOnly = false, requireChoice = true } = {}) {
    if (!picker) return;
    for (const [index, fieldset] of [...picker.querySelectorAll('fieldset.variant-option')].entries()) {
      const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
      if (!radios.length) continue;
      const name = radios[0].dataset.optionName || fieldset.querySelector('legend')?.textContent.trim() || '';
      if (sizeOnly && !/talla|size/i.test(name)) continue;
      const wrapper = document.createElement('div');
      wrapper.className = 'variant-option variant-option--dropdowns quick-add-option';
      wrapper.dataset.key = `quick-add-option-${index}`;
      const label = document.createElement('label');
      const select = document.createElement('select');
      select.id = `${sizeOnly ? 'ProductSize' : 'QuickAdd'}-${picker.getAttribute('data-product-id')}-${index}`;
      select.name = `options[${name}]`;
      select.className = 'quick-add-option__select';
      select.dataset.key = `quick-add-select-${index}`;
      label.htmlFor = select.id;
      // Keep the original option name in form data; only customize its label.
      label.textContent = fieldset.querySelector('[data-size-label]')?.textContent || name;
      if (fieldset.hasAttribute('data-size-option')) {
        wrapper.setAttribute('data-size-option', '');
        label.setAttribute('data-size-label', '');
      }
      for (const radio of radios) {
        const option = document.createElement('option');
        option.value = radio.value;
        // The server's localized label includes sold-out status. Keep those
        // options selectable, as Horizon does, to resolve multi-option products.
        option.textContent = radio.getAttribute('aria-label') || radio.value;
        if (radio.closest('.variant-option__button-label--half-size')) option.dataset.halfSize = '';
        for (const attribute of radio.attributes) {
          if (attribute.name.startsWith('data-')) option.setAttribute(attribute.name, attribute.value);
        }
        option.defaultSelected = radio.checked;
        option.selected = radio.checked;
        select.append(option);
      }
      wrapper.append(label, select);
      fieldset.replaceWith(wrapper);
    }
    // Respect templates that already use dropdowns, while avoiding duplicate
    // label IDs if the same product is also present behind the dialog.
    for (const select of picker.querySelectorAll('select')) {
      const isSize = /talla|size/i.test(select.options[0]?.dataset.optionName || select.name);
      if (sizeOnly && !isSize) continue;
      if (isSize) {
        picker.setAttribute('data-size-required', '');
        select.dataset.sizeSelect = '';
        if (requireChoice) {
          picker.dataset.sizeChosen = 'false';
          for (const option of select.options) { option.selected = false; option.removeAttribute('selected'); }
          let placeholder = select.querySelector('[data-size-placeholder]');
          if (!placeholder) {
            placeholder = document.createElement('option');
            placeholder.value = '';
            placeholder.disabled = true;
            placeholder.dataset.sizePlaceholder = '';
            placeholder.textContent = picker.dataset.sizePrompt || Theme.translations.product_choose_size;
            select.prepend(placeholder);
          }
          placeholder.selected = true;
          placeholder.defaultSelected = true;
        }
      }
      select.classList.add('quick-add-option__select');
      const wrapper = select.closest('.variant-option');
      wrapper?.classList.add('quick-add-option');
      if (select.id && !/^(QuickAdd|ProductSize)-/.test(select.id)) {
        const oldId = select.id;
        select.id = `QuickAdd-${oldId}`;
        for (const label of wrapper?.querySelectorAll('label') || []) {
          if (label.htmlFor === oldId) label.htmlFor = select.id;
        }
      }
      if (select.closest('quick-add-select') || !wrapper) continue;
      const label = [...wrapper.querySelectorAll('label')].find((item) => item.htmlFor === select.id);
      if (!label) continue;
      label.id = `${select.id}-label`;
      const dropdown = document.createElement('quick-add-select');
      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.id = `${select.id}-trigger`;
      trigger.className = 'quick-add-option__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', `${select.id}-list`);
      trigger.setAttribute('aria-labelledby', `${label.id} ${select.id}-value`);
      // Morph may keep an existing custom element connected, so its incoming
      // label association must already match the active presentation.
      label.htmlFor = trigger.id;
      const value = document.createElement('span');
      value.id = `${select.id}-value`;
      value.textContent = select.selectedOptions[0]?.textContent || '';
      value.toggleAttribute('data-size-choice-prompt-value', !!select.selectedOptions[0]?.hasAttribute('data-size-placeholder'));
      trigger.append(value);
      const list = document.createElement('div');
      list.id = `${select.id}-list`;
      list.className = 'quick-add-option__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-labelledby', label.id);
      list.hidden = true;
      if (typeof list.showPopover === 'function') list.setAttribute('popover', 'manual');
      for (const [index, option] of [...select.options].entries()) {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(option.selected));
        button.dataset.value = option.value;
        const sizeNumber = Number(option.value.replace(',', '.').replace('½', '.5').replace(/\s/g, ''));
        if (option.hasAttribute('data-half-size') ||
            (isSize && Number.isFinite(sizeNumber) && Math.abs(sizeNumber % 1 - 0.5) < 0.001)) {
          button.dataset.halfSize = '';
        }
        button.textContent = option.textContent;
        button.disabled = option.disabled;
        button.tabIndex = -1;
        button.style.setProperty('--menu-item-order', String(index));
        list.append(button);
      }
      select.before(dropdown);
      dropdown.append(select, trigger, list);
    }
  }

  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());
  #handleChange = (event) => this.variantChanged(event);
  #confirmedPicker;
  #applyingResponse = false;
  #desktopMarkup;
  #sizeMedia = window.matchMedia('(max-width: 749px)');

  get requiresSizeChoice() {
    return this.hasAttribute('data-size-required') && (!!this.closest('quick-add-dialog') ||
      (this.dataset.templateProductMatch === 'true' && !this.closest('product-card')));
  }

  get sizeSelectionBlocked() {
    return this.requiresSizeChoice && (this.dataset.sizeChosen !== 'true' || this.hasAttribute('data-size-pending'));
  }

  #notifySizeRequirement() {
    this.dispatchEvent(new CustomEvent('size:requirement', { bubbles: true }));
  }

  resetSizeChoice() {
    if (!this.hasAttribute('data-size-required')) return;
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.removeAttribute('data-size-pending');
    this.dataset.sizeChosen = 'false';
    const source = this.cloneNode(true);
    // Rebuild custom dropdowns as well as their native data source.
    for (const dropdown of source.querySelectorAll('quick-add-select')) dropdown.replaceWith(dropdown.querySelector('select'));
    for (const label of source.querySelectorAll('label[for$="-trigger"]')) label.htmlFor = label.htmlFor.replace(/-trigger$/, '');
    VariantPicker.prepareQuickAdd(source);
    morph(this, source);
    this.#confirmedPicker = this.cloneNode(true);
    this.#notifySizeRequirement();
  }

  #applySizePresentation = () => {
    if (this.closest('quick-add-dialog, product-card') || this.dataset.templateProductMatch !== 'true' ||
        !this.hasAttribute('data-size-required')) return;
    if (!this.#desktopMarkup) this.#desktopMarkup = this.cloneNode(true);
    const source = this.#desktopMarkup.cloneNode(true);
    if (this.#sizeMedia.matches) {
      this.setAttribute('data-mobile-size', '');
      VariantPicker.prepareQuickAdd(source, { sizeOnly: true, requireChoice: this.dataset.sizeChosen !== 'true' });
    } else {
      this.removeAttribute('data-mobile-size');
      if (this.dataset.sizeChosen !== 'true') {
        for (const radio of source.querySelectorAll('input[type="radio"][data-option-name]')) {
          if (!/talla|size/i.test(radio.dataset.optionName)) continue;
          radio.checked = false;
          radio.removeAttribute('checked');
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
        // Templates configured with desktop dropdowns need the same prompt.
        for (const select of source.querySelectorAll('select')) {
          if (!/talla|size/i.test(select.name)) continue;
          for (const option of select.options) { option.selected = false; option.removeAttribute('selected'); }
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.disabled = true;
          placeholder.dataset.sizePlaceholder = '';
          placeholder.textContent = this.dataset.sizePrompt || Theme.translations.product_choose_size;
          placeholder.defaultSelected = true;
          select.querySelector('[data-size-placeholder]')?.remove();
          select.prepend(placeholder);
          select.value = '';
        }
      }
    }
    morph(this, source);
    if (this.dataset.sizeChosen !== 'true') {
      // Morph preserves pill-animation attributes, so explicitly clear the
      // real inputs too; the first desktop size must not look selected.
      for (const radio of this.querySelectorAll('input[type="radio"][data-option-name]')) {
        if (!/talla|size/i.test(radio.dataset.optionName)) continue;
        radio.checked = false;
        radio.removeAttribute('checked');
        radio.dataset.currentChecked = 'false';
        radio.dataset.previousChecked = 'false';
      }
    }
    this.#refreshRadios();
    this.#confirmedPicker = this.cloneNode(true);
    this.#notifySizeRequirement();
  };

  #refreshRadios() {
    this.#radios = [];
    this.#checkedIndices = [];
    for (const fieldset of this.refs.fieldsets || []) {
      const radios = [...fieldset.querySelectorAll('input')];
      this.#radios.push(radios);
      const index = radios.findIndex((radio) => radio.checked);
      this.#checkedIndices.push(index < 0 ? [] : [index]);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#applySizePresentation();
    this.#refreshRadios();
    if (!this.#confirmedPicker) this.#confirmedPicker = this.cloneNode(true);
    this.addEventListener('change', this.#handleChange);
    this.#resizeObserver.observe(this);
    this.#sizeMedia.addEventListener('change', this.#applySizePresentation);
    queueMicrotask(() => this.isConnected && this.#notifySizeRequirement());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('change', this.#handleChange);
    this.#sizeMedia.removeEventListener('change', this.#applySizePresentation);
    // Morph can temporarily detach/reinsert a picker while committing a valid
    // response. Abort only a genuinely removed instance, not that commit.
    queueMicrotask(() => {
      if (!this.isConnected && !this.#applyingResponse) this.#abortController?.abort();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refreshRadios();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (selectedOption.hasAttribute('data-size-placeholder')) return;
    if (/talla|size/i.test(selectedOption.dataset.optionName || '')) {
      this.dataset.sizeChosen = 'true';
    }

    this.updateSelectedOption(event.target);
    // Other options may change before a size has been chosen. Keep the local
    // selection, but never ask Shopify to resolve a placeholder as an option ID.
    if (this.requiresSizeChoice && this.dataset.sizeChosen !== 'true') return;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
      nextUrl: isOnProductPage ? url.href : undefined,
    });
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} [options.nextUrl] - Commit this address only after a successful response.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {}, nextUrl } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    const timeout = setTimeout(() => controller.abort(new DOMException('Variant request timed out', 'TimeoutError')), 15000);
    const requestedValues = this.selectedOptionsValues;
    if (this.requiresSizeChoice) this.setAttribute('data-size-pending', '');
    this.#notifySizeRequirement();
    this.querySelector('[data-variant-load-error]')?.remove();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    // Some optional consumers aren't connected yet; rejection must still be
    // handled while existing listeners receive the same rejected promise.
    void deferredEventPromise.promise.catch(() => {});
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Variant response: ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (controller.signal.aborted || this.#abortController !== controller || !this.isConnected) {
          throw new DOMException('Superseded', 'AbortError');
        }
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) throw new Error('Missing variant response');
        // Validate before morphing: an error document must not replace controls.
        const variantData = JSON.parse(textContent);
        const productViewAttr = variantPickerJsonScript
          ?.closest('[view-event-payload]')?.getAttribute('view-event-payload')?.trim();
        const selectedVariant = productViewAttr ? JSON.parse(productViewAttr)?.product?.selectedVariant : null;

        let newProduct;

        this.#applyingResponse = true;
        try {
          if (morphElementSelector === 'main') {
            this.updateMain(html);
          } else if (morphElementSelector) {
            this.updateElement(html, morphElementSelector);
          } else {
            const { overflowList } = this.refs;
            const wasSwatchesExpanded =
              overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

            newProduct = this.updateVariantPicker(html);

            if (wasSwatchesExpanded) {
              const overflowListAfterMorph = overflowList;
              if (overflowListAfterMorph instanceof OverflowList) {
                overflowListAfterMorph.showAll();
              }
            }
          }
        } finally { this.#applyingResponse = false; }
        this.#confirmedPicker = this.cloneNode(true);
        if (nextUrl && nextUrl !== location.href) history.replaceState(history.state, '', nextUrl);

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          if (variantData && typeof variantData === 'object') {
            deferredEventPromise.resolve({
              variant: selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (this.#abortController !== controller || !this.isConnected || error.name === 'AbortError') return;
        this.#pendingRequestUrl = undefined;
        if (this.#confirmedPicker) morph(this, this.#confirmedPicker.cloneNode(true));
        this.dataset.sizeChosen = this.#confirmedPicker?.dataset.sizeChosen || 'false';
        for (const radio of this.querySelectorAll('input[type="radio"]')) {
          radio.dataset.currentChecked = String(radio.checked);
          radio.dataset.previousChecked = 'false';
        }
        this.#refreshRadios();
        const notice = document.createElement('div');
        notice.dataset.variantLoadError = '';
        notice.setAttribute('role', 'alert');
        const message = document.createElement('p');
        message.textContent = Theme.translations.product_variant_error;
        const retry = document.createElement('button');
        retry.type = 'button';
        retry.className = 'button button-secondary';
        retry.textContent = Theme.translations.product_retry;
        retry.addEventListener('click', () => {
          for (const option of this.querySelectorAll('[data-option-value-id]')) {
            if (!requestedValues.includes(option.dataset.optionValueId)) continue;
            if (/talla|size/i.test(option.dataset.optionName || '')) this.dataset.sizeChosen = 'true';
            if (option instanceof HTMLOptionElement) {
              option.parentElement.value = option.value;
              this.updateSelectedOption(option.parentElement);
            } else this.updateSelectedOption(option);
          }
          this.fetchUpdatedSection(requestUrl, { morphElementSelector, detail, nextUrl });
        }, { once: true });
        notice.append(message, retry);
        this.append(notice);
      })
      .finally(() => {
        clearTimeout(timeout);
        if (this.#abortController === controller) this.#abortController = undefined;
        if (!this.#abortController) {
          this.removeAttribute('data-size-pending');
          this.#notifySizeRequirement();
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (!this.closest('quick-add-dialog, product-card') && this.dataset.templateProductMatch === 'true') {
      this.#desktopMarkup = newVariantPickerSource.cloneNode(true);
    }
    if (this.closest('quick-add-dialog') || this.hasAttribute('data-mobile-size')) {
      VariantPicker.prepareQuickAdd(newVariantPickerSource, {
        sizeOnly: !this.closest('quick-add-dialog'), requireChoice: this.dataset.sizeChosen !== 'true',
      });
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#refreshRadios();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected]:not([data-size-placeholder]), fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.filter((option) => !option.hasAttribute('data-size-placeholder')).map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

/** Shared overlay picker; the hidden native select remains the variant data source. */
class QuickAddSelect extends HTMLElement {
  #controller;
  #mobile = window.matchMedia('(max-width: 749px)');
  #frame = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const options = { signal: this.#controller.signal };
    this.addEventListener('click', this.#click, options);
    this.addEventListener('keydown', this.#keydown, options);
    this.addEventListener('change', this.#sync, options);
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#close();
    }, options);
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.#close();
    }, options);
    this.closest('dialog')?.addEventListener('close', () => this.#close(), options);
    this.#mobile.addEventListener('change', this.#setMode, options);
    window.addEventListener('resize', this.#schedulePosition, options);
    document.addEventListener('scroll', this.#schedulePosition, { ...options, capture: true });
    window.visualViewport?.addEventListener('resize', this.#schedulePosition, options);
    window.visualViewport?.addEventListener('scroll', this.#schedulePosition, options);
    this.#setMode();
  }

  disconnectedCallback() { this.#close(); this.#controller?.abort(); }

  get trigger() { return this.querySelector('.quick-add-option__trigger'); }
  get list() { return this.querySelector('.quick-add-option__list'); }
  get options() { return [...this.querySelectorAll('[role="option"]:not(:disabled)')]; }
  get enabled() { return !!this.closest('quick-add-dialog') || this.#mobile.matches; }

  #setMode = () => {
    this.#close();
    const select = this.querySelector('select');
    const label = this.closest('.quick-add-option')?.querySelector('label');
    if (label && select && this.trigger) label.htmlFor = this.enabled ? this.trigger.id : select.id;
  };

  #sync = () => {
    const select = this.querySelector('select');
    const value = this.trigger?.querySelector('span');
    if (!select) return;
    if (value) {
      value.textContent = select.selectedOptions[0]?.textContent || '';
      value.toggleAttribute('data-size-choice-prompt-value', !!select.selectedOptions[0]?.hasAttribute('data-size-placeholder'));
    }
    for (const option of this.querySelectorAll('[role="option"]')) {
      option.setAttribute('aria-selected', String(option.dataset.value === select.value));
    }
  };

  #open(last = false) {
    if (!this.enabled || !this.list || !this.trigger) return;
    this.#sync();
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    if (this.list.hasAttribute('popover')) {
      try { this.list.showPopover(); } catch { this.list.removeAttribute('popover'); }
    }
    this.setAttribute('data-menu-open', '');
    this.#position();
    const options = this.options;
    const selected = options.find((item) => item.getAttribute('aria-selected') === 'true');
    const active = last ? options.at(-1) : selected || options[0];
    active?.focus({ preventScroll: true });
    // Scroll only the small list, never the page or the whole popup.
    if (active) this.list.scrollTop = active.offsetTop - this.list.clientHeight / 2;
  }

  #close(restoreFocus = false) {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    if (!this.list || !this.trigger) return;
    if (this.list.hasAttribute('popover') && this.list.matches(':popover-open')) this.list.hidePopover();
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-menu-open');
    if (restoreFocus) this.trigger.focus({ preventScroll: true });
  }

  #click = (event) => {
    if (!this.enabled || !(event.target instanceof Element)) return;
    if (event.target.closest('.quick-add-option__trigger')) {
      this.list?.hidden ? this.#open() : this.#close(true);
      return;
    }
    const option = event.target.closest('[role="option"]');
    const select = this.querySelector('select');
    if (!option || option.disabled || !select) return;
    const changed = select.value !== option.dataset.value;
    select.value = option.dataset.value;
    this.#sync();
    this.#close(true);
    if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #keydown = (event) => {
    if (!this.enabled || !this.list) return;
    if (event.key === 'Escape' && !this.list.hidden) {
      event.preventDefault();
      event.stopPropagation();
      this.#close(true);
      return;
    }
    if (event.key === 'Tab') {
      // Return to the trigger before normal Tab navigation leaves the list.
      if (!this.list.hidden) this.#close(true);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    if (this.list.hidden) {
      this.#open(event.key === 'End' || event.key === 'ArrowUp');
      return;
    }
    const options = this.options;
    const index = options.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1
      : Math.max(0, Math.min(options.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
    const active = options[next];
    active?.focus({ preventScroll: true });
    if (active) this.list.scrollTop = active.offsetTop - this.list.clientHeight / 2;
  };

  #schedulePosition = (event) => {
    if (!this.list || this.list.hidden || this.list.contains(event?.target) || this.#frame) return;
    this.#frame = requestAnimationFrame(() => { this.#frame = 0; this.#position(); });
  };

  #position() {
    const list = this.list;
    if (!list || list.hidden || !this.trigger) return;
    const viewport = window.visualViewport;
    const gap = 8;
    let left = (viewport?.offsetLeft || 0) + gap;
    let top = (viewport?.offsetTop || 0) + gap;
    let right = left + (viewport?.width || document.documentElement.clientWidth) - gap * 2;
    let bottom = top + (viewport?.height || window.innerHeight) - gap * 2;
    const anchor = this.trigger.getBoundingClientRect();
    const floating = list.hasAttribute('popover');
    if (!this.closest('quick-add-dialog')) {
      const section = this.closest('[data-testid="product-information"]')?.getBoundingClientRect();
      if (section) {
        left = Math.max(left, section.left + gap); right = Math.min(right, section.right - gap);
        top = Math.max(top, section.top + gap); bottom = Math.min(bottom, section.bottom - gap);
      }
    }
    // Older browsers retain an absolutely positioned overlay constrained to
    // the popup's scroll area; modern browsers escape clipping via top layer.
    if (!floating) {
      const clip = this.closest('.quick-add-modal__content')?.getBoundingClientRect();
      if (clip) {
        left = Math.max(left, clip.left + gap); right = Math.min(right, clip.right - gap);
        top = Math.max(top, clip.top + gap); bottom = Math.min(bottom, clip.bottom - gap);
      }
    }
    if (anchor.bottom < top || anchor.top > bottom || right <= left || bottom <= top) { this.#close(); return; }
    const below = Math.max(0, bottom - anchor.bottom - gap);
    const above = Math.max(0, anchor.top - top - gap);
    const openAbove = below < 240 && above > below;
    const maxHeight = Math.min(360, openAbove ? above : below);
    const width = Math.min(Math.max(anchor.width, 260), right - left);
    list.style.width = `${width}px`;
    list.style.maxHeight = `${maxHeight}px`;
    const height = Math.min(list.scrollHeight + 2, maxHeight);
    let x = Math.max(left, Math.min(anchor.right - width, right - width));
    let y = Math.max(top, Math.min(openAbove ? anchor.top - gap - height : anchor.bottom + gap, bottom - height));
    if (!floating) {
      const origin = this.getBoundingClientRect();
      x -= origin.left; y -= origin.top;
    }
    list.style.left = `${x}px`;
    list.style.top = `${y}px`;
  }
}

if (!customElements.get('quick-add-select')) {
  customElements.define('quick-add-select', QuickAddSelect);
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
